import React, { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { api } from '../api';
import {
  MessageCircle,
  Play,
  Pause,
  RotateCcw,
  CheckCircle,
  AlertCircle,
  Clock,
  Star,
  Mic,
  MicOff
} from 'lucide-react';
import toast from 'react-hot-toast';
import { authHeader } from '../auth';
import AccessGateModal from '../components/AccessGateModal';
import useAccessGate from '../hooks/useAccessGate';

const PageContainer = styled.div`
  min-height: 100vh;
  background: linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%);
  padding: 2rem 1rem;
`;

const Container = styled.div`
  max-width: 1000px;
  margin: 0 auto;
`;

const PageTitle = styled.h1`
  text-align: center;
  font-size: 2.5rem;
  font-weight: 700;
  color: #1f2937;
  margin-bottom: 1rem;
`;

const PageSubtitle = styled.p`
  text-align: center;
  font-size: 1.1rem;
  color: #6b7280;
  margin-bottom: 3rem;
  max-width: 600px;
  margin-left: auto;
  margin-right: auto;
`;

const SetupSection = styled.div`
  background: white;
  border-radius: 20px;
  padding: 3rem;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.1);
  margin-bottom: 2rem;

  @media (max-width: 640px) {
    padding: 2rem 1.5rem;
  }
`;

const InputGroup = styled.div`
  margin-bottom: 2rem;
`;

const Label = styled.label`
  display: block;
  font-weight: 600;
  color: #1f2937;
  margin-bottom: 0.5rem;
`;

const Input = styled.input`
  width: 100%;
  padding: 1rem;
  border: 2px solid #e5e7eb;
  border-radius: 10px;
  font-size: 1rem;
  transition: border-color 0.3s;

  &:focus {
    outline: none;
    border-color: #6366f1;
  }
`;

const Select = styled.select`
  width: 100%;
  padding: 1rem;
  border: 2px solid #e5e7eb;
  border-radius: 10px;
  font-size: 1rem;
  background: white;
  transition: border-color 0.3s;

  &:focus {
    outline: none;
    border-color: #6366f1;
  }
`;

const StartButton = styled.button`
  background: linear-gradient(135deg, #6366f1, #8b5cf6);
  color: white;
  border: none;
  padding: 1rem 2rem;
  border-radius: 50px;
  font-size: 1.1rem;
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin: 0 auto;
  transition: all 0.3s;

  &:hover:not(:disabled) {
    transform: translateY(-2px);
    box-shadow: 0 10px 25px rgba(99, 102, 241, 0.3);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const InterviewSection = styled.div`
  background: white;
  border-radius: 20px;
  padding: 3rem;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.1);
  margin-bottom: 2rem;
`;

const QuestionCard = styled.div`
  border: 2px solid #0ea5e9;
  border-radius: 15px;
  padding: 2rem;
  margin-bottom: 2rem;
  position: relative;
`;

const QuestionNumber = styled.div`
  position: absolute;
  top: -15px;
  left: 20px;
  background: #0ea5e9;
  color: white;
  width: 30px;
  height: 30px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 600;
  font-size: 0.9rem;
`;

const QuestionText = styled.h3`
  font-size: 1.3rem;
  font-weight: 600;
  color: #1f2937;
  margin-bottom: 1rem;
  margin-top: 1rem;
`;

const QuestionCategory = styled.div`
  background: #0ea5e9;
  color: white;
  padding: 0.25rem 0.75rem;
  border-radius: 20px;
  font-size: 0.8rem;
  font-weight: 500;
  display: inline-block;
  margin-bottom: 1rem;
`;

const AnswerSection = styled.div`
  margin-top: 2rem;
`;

const AnswerTextarea = styled.textarea`
  width: 100%;
  min-height: 120px;
  padding: 1rem;
  border: 2px solid #e5e7eb;
  border-radius: 10px;
  font-size: 1rem;
  font-family: inherit;
  resize: vertical;
  transition: border-color 0.3s;

  &:focus {
    outline: none;
    border-color: #6366f1;
  }
`;

const Timer = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  color: #6b7280;
  font-weight: 500;
  margin-bottom: 1rem;
`;

const Controls = styled.div`
  display: flex;
  gap: 1rem;
  justify-content: center;
  margin-top: 2rem;
  flex-wrap: wrap;
`;

const ControlButton = styled.button`
  background: ${props => (props.primary ? 'linear-gradient(135deg, #6366f1, #8b5cf6)' : 'white')};
  color: ${props => (props.primary ? 'white' : '#6366f1')};
  border: 2px solid #6366f1;
  padding: 0.75rem 1.5rem;
  border-radius: 50px;
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  transition: all 0.3s;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 5px 15px rgba(99, 102, 241, 0.3);
  }
`;

const ResultsSection = styled.div`
  background: white;
  border-radius: 20px;
  padding: 3rem;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.1);
`;

const ScoreCard = styled.div`
  background: linear-gradient(135deg, #f0fdf4, #dcfce7);
  border: 2px solid #22c55e;
  border-radius: 15px;
  padding: 2rem;
  text-align: center;
  margin-bottom: 2rem;
`;

const Score = styled.div`
  font-size: 3rem;
  font-weight: 800;
  color: #22c55e;
  margin-bottom: 0.5rem;
`;

const ScoreLabel = styled.div`
  font-size: 1.2rem;
  color: #166534;
  font-weight: 600;
`;

const FeedbackCard = styled.div`
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 15px;
  padding: 1.5rem;
  margin-bottom: 1.5rem;
`;

const FeedbackTitle = styled.h3`
  font-size: 1.2rem;
  font-weight: 600;
  color: #1f2937;
  margin-bottom: 1rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const FeedbackContent = styled.div`
  color: #4b5563;
  line-height: 1.6;
`;

const InterviewSimulator = () => {
  const { gate, requirePremium, closeGate } = useAccessGate();
  const [position, setPosition] = useState('');
  const [experience, setExperience] = useState('');
  const [isInterviewActive, setIsInterviewActive] = useState(false);
  const [questions, setQuestions] = useState([]);
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [answers, setAnswers] = useState([]);
  const [timeLeft, setTimeLeft] = useState(150);
  const [isRecording, setIsRecording] = useState(false);
  const [showResults, setShowResults] = useState(false);
  const [interviewResults, setInterviewResults] = useState(null);
  const [startingInterview, setStartingInterview] = useState(false);
  const [evaluating, setEvaluating] = useState(false);
  const timerRef = useRef(null);

  const clearTimer = () => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
  };

  const startInterview = async () => {
    const allowed = await requirePremium();
    if (!allowed) return;
    if (!position.trim()) {
      toast.error('Lütfen pozisyon bilgisini girin');
      return;
    }
    setStartingInterview(true);
    try {
      const { data } = await api.post(
        '/api/interview/start',
        { position, experience },
        { headers: authHeader() }
      );
      const fetched = data?.questions || [];
      if (!fetched.length) {
        toast.error('Soru alınamadı');
        return;
      }
      setQuestions(fetched);
      setIsInterviewActive(true);
      setCurrentQuestion(0);
      setAnswers([]);
      setTimeLeft(fetched[0]?.timeLimit || 150);
      setShowResults(false);
      clearTimer();
      timerRef.current = setInterval(() => {
        setTimeLeft(prev => {
          if (prev <= 1) {
            clearTimer();
            nextQuestion();
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    } catch (e) {
      toast.error('Soru oluşturuurken bir hata oluştu');
    } finally {
      setStartingInterview(false);
    }
  };

  const nextQuestion = () => {
    if (currentQuestion < (questions?.length || 0) - 1) {
      setCurrentQuestion(prev => prev + 1);
      setTimeLeft(questions[currentQuestion + 1]?.timeLimit || 150);
      clearTimer();
      timerRef.current = setInterval(() => {
        setTimeLeft(prev => {
          if (prev <= 1) {
            clearTimer();
            nextQuestion();
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    } else {
      finishInterview();
    }
  };

  const finishInterview = async () => {
    setIsInterviewActive(false);
    clearTimer();
    setEvaluating(true);
    try {
      const payload = { questions, answers, position };
      const { data } = await api.post(
        '/api/interview/evaluate',
        payload,
        { headers: authHeader() }
      );
      setInterviewResults(data);
      setShowResults(true);
      toast.success('Mülakat Tamamlandı');
    } catch (e) {
      setShowResults(true);
      setInterviewResults({
        score: 75,
        feedback: {
          strengths: ['Net anlatım'],
          improvements: ['Daha fazla örnek'],
          overall:
            'Değerlendirme sırasında hata oluştu; Örnek sonular gösterildi.'
        }
      });
      toast.error('Değerlendirme sırasında bir hata oluştu');
    } finally {
      setEvaluating(false);
    }
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const handleAnswerChange = (answer) => {
    const newAnswers = [...answers];
    newAnswers[currentQuestion] = answer;
    setAnswers(newAnswers);
  };

  return (
    <PageContainer>
      <Container>
        <PageTitle>Mülakat Simülasyonu</PageTitle>
        <PageSubtitle>
          AI ile gerçekçi mülakat deneyimi yaşayın ve kendinizi geliştirin.
        </PageSubtitle>

        {!isInterviewActive && !showResults && (
          <SetupSection>
            <InputGroup>
              <Label>Başvuracağınız Pozisyon *</Label>
              <Input
                placeholder="Örn: Yazılım Geliştirici, Pazarlama Uzmanı..."
                value={position}
                onChange={(e) => setPosition(e.target.value)}
              />
            </InputGroup>

            <InputGroup>
              <Label>Deneyim Seviyeniz</Label>
              <Select
                value={experience}
                onChange={(e) => setExperience(e.target.value)}
              >
                <option value="">Seçiniz</option>
                <option value="yeni-mezun">Yeni Mezun (0-1 yıl)</option>
                <option value="junior">Junior (1-3 yıl)</option>
                <option value="mid-level">Mid-Level (3-5 yıl)</option>
                <option value="senior">Senior (5+ yıl)</option>
              </Select>
            </InputGroup>

            <StartButton onClick={startInterview} disabled={startingInterview}>
              {startingInterview ? (
                <>
                  <span
                    className="animate-spin"
                    style={{
                      width: 18,
                      height: 18,
                      borderRadius: '999px',
                      border: '2px solid rgba(255,255,255,0.7)',
                      borderTopColor: 'transparent'
                    }}
                  />
                  Sorular hazırlanıyor...
                </>
              ) : (
                <>
                  <Play size={20} />
                  Mülakata Başla
                </>
              )}
            </StartButton>
          </SetupSection>
        )}

        {isInterviewActive && questions.length > 0 && (
          <InterviewSection>
            <QuestionCard>
              <QuestionNumber>{currentQuestion + 1}</QuestionNumber>
              <QuestionCategory>{questions[currentQuestion]?.category}</QuestionCategory>
              <QuestionText>{questions[currentQuestion]?.text}</QuestionText>

              <Timer>
                <Clock size={16} />
                Kalan süre: {formatTime(timeLeft)}
              </Timer>
            </QuestionCard>

            <AnswerSection>
              <Label>Cevabınız:</Label>
              <AnswerTextarea
                placeholder="Cevabınızı buraya yazın..."
                value={answers[currentQuestion] || ''}
                onChange={(e) => handleAnswerChange(e.target.value)}
              />

              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '1rem',
                  marginTop: '1rem'
                }}
              >
                <ControlButton onClick={() => setIsRecording(!isRecording)}>
                  {isRecording ? <MicOff size={16} /> : <Mic size={16} />}
                  {isRecording ? 'Kaydet Durdur' : 'Sesli Kayıt'}
                </ControlButton>
              </div>
            </AnswerSection>

            <Controls>
              <ControlButton onClick={nextQuestion}>
                {currentQuestion === questions.length - 1
                  ? 'Mülakatı Bitir'
                  : 'Sonraki Soru'}
              </ControlButton>
              <ControlButton onClick={() => setIsInterviewActive(false)}>
                <Pause size={16} />
                Mülakatı Durdur
              </ControlButton>
            </Controls>
          </InterviewSection>
        )}

        {evaluating && (
          <ResultsSection>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '0.75rem',
                color: '#6366f1'
              }}
            >
              <span
                className="animate-spin"
                style={{
                  width: 22,
                  height: 22,
                  borderRadius: '999px',
                  border: '2px solid rgba(99,102,241,0.4)',
                  borderTopColor: '#6366f1'
                }}
              />
              <span>Cevaplarınız değerlendiriliyor...</span>
            </div>
          </ResultsSection>
        )}

        {showResults && interviewResults && !evaluating && (
          <ResultsSection>
            <ScoreCard>
              <Score>{interviewResults.score}/100</Score>
              <ScoreLabel>Mülakat Puanınız</ScoreLabel>
            </ScoreCard>

            <FeedbackCard>
              <FeedbackTitle>
                <CheckCircle size={20} color="#22c55e" />
                Güçlü Yönleriniz
              </FeedbackTitle>
              <FeedbackContent>
                <ul style={{ paddingLeft: '1.5rem' }}>
                  {interviewResults.feedback.strengths.map((strength, index) => (
                    <li key={index} style={{ marginBottom: '0.5rem' }}>
                      {strength}
                    </li>
                  ))}
                </ul>
              </FeedbackContent>
            </FeedbackCard>

            <FeedbackCard>
              <FeedbackTitle>
                <AlertCircle size={20} color="#f59e0b" />
                İyileştirme Önerileri
              </FeedbackTitle>
              <FeedbackContent>
                <ul style={{ paddingLeft: '1.5rem' }}>
                  {interviewResults.feedback.improvements.map((improvement, index) => (
                    <li key={index} style={{ marginBottom: '0.5rem' }}>
                      {improvement}
                    </li>
                  ))}
                </ul>
              </FeedbackContent>
            </FeedbackCard>

            <FeedbackCard>
              <FeedbackTitle>
                <Star size={20} color="#6366f1" />
                Genel Değerlendirme
              </FeedbackTitle>
              <FeedbackContent>
                {interviewResults.feedback.overall}
              </FeedbackContent>
            </FeedbackCard>

            <Controls>
              <ControlButton primary onClick={() => window.location.reload()}>
                <RotateCcw size={16} />
                Yeni Mülakat
              </ControlButton>
            </Controls>
          </ResultsSection>
        )}
      </Container>
      <AccessGateModal type={gate} onClose={closeGate} />
    </PageContainer>
  );
};

export default InterviewSimulator;

